import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { buildNavItems, buildCrumbs } from "../lib/navigation";
import { SectionErrorBoundary } from "./SectionErrorBoundary";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "./ui/sheet";
import { LogOut, Ambulance, ShieldCheck, Menu, ChevronDown, ArrowLeft, ChevronRight } from "lucide-react";
import { cn } from "../lib/utils";

const NavButton = ({ item, active, onNavigate, testPrefix, nested }) => {
  const Icon = item.icon;
  return (
    <button
      data-testid={`${testPrefix}-${item.label.toLowerCase().replace(/\s/g, "-")}`}
      onClick={() => onNavigate(item.to)}
      className={cn(
        "w-full flex items-center gap-3 rounded-md py-2.5 text-sm font-medium transition-colors",
        nested ? "px-3 pl-9" : "px-3",
        active ? "bg-primary text-white border-l-4 border-nexus-gold" : "text-slate-600 hover:bg-slate-100 hover:text-primary"
      )}
    >
      <Icon className="h-4 w-4" /> {item.label}
    </button>
  );
};

const NavGroup = ({ item, pathname, onNavigate, testPrefix }) => {
  const childActive = item.children.some((c) => c.to === pathname);
  const [open, setOpen] = useState(childActive);
  useEffect(() => { if (childActive) setOpen(true); }, [childActive]);
  const Icon = item.icon;
  return (
    <div>
      <button
        data-testid={`${testPrefix}-group-${item.group.toLowerCase()}`}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "w-full flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
          childActive ? "text-primary" : "text-slate-600 hover:bg-slate-100"
        )}
      >
        <Icon className="h-4 w-4" /> {item.group}
        <ChevronDown className={cn("h-4 w-4 ml-auto transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="mt-1 space-y-1">
          {item.children.map((c) => (
            <NavButton key={c.to} item={c} active={pathname === c.to} onNavigate={onNavigate} testPrefix={testPrefix} nested />
          ))}
        </div>
      )}
    </div>
  );
};

const NavList = ({ items, pathname, onNavigate, testPrefix }) => (
  <nav className="flex-1 px-3 space-y-1">
    {items.map((item) =>
      item.group
        ? <NavGroup key={item.group} item={item} pathname={pathname} onNavigate={onNavigate} testPrefix={testPrefix} />
        : <NavButton key={item.to} item={item} active={pathname === item.to} onNavigate={onNavigate} testPrefix={testPrefix} />
    )}
  </nav>
);

const Breadcrumbs = ({ crumbs, onNavigate }) => (
  <nav data-testid="breadcrumbs" aria-label="Percorso" className="hidden md:flex items-center gap-1 text-xs text-white/70 min-w-0 truncate">
    {crumbs.map((c, i) => {
      const last = i === crumbs.length - 1;
      return (
        <span key={`${c.label}-${i}`} className="flex items-center gap-1 min-w-0">
          {i > 0 && <ChevronRight className="h-3 w-3 text-white/40 shrink-0" />}
          {c.to && !last ? (
            <button type="button" data-testid={`crumb-${i}`} onClick={() => onNavigate(c.to)} className="hover:text-nexus-gold hover:underline truncate">{c.label}</button>
          ) : (
            <span data-testid={`crumb-${i}`} className={cn("truncate", last && "font-semibold text-nexus-gold")}>{c.label}</span>
          )}
        </span>
      );
    })}
  </nav>
);

export const AppLayout = ({ children, crumbLabel }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const isOperatore = isAdmin && user?.staff_role === "operatore";
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = useMemo(() => buildNavItems({ isAdmin, isOperatore }), [isAdmin, isOperatore]);

  const RoleBadge = ({ dark }) => (
    <div className={cn(
      "flex items-center gap-2 rounded-md px-3 py-2 text-xs font-semibold",
      isAdmin
        ? (dark ? "bg-white/10 text-nexus-gold border border-nexus-gold/50" : "bg-primary/10 text-primary border border-primary/20")
        : "bg-nexus-red text-white"
    )}>
      {isAdmin ? <ShieldCheck className="h-4 w-4" /> : <Ambulance className="h-4 w-4" />}
      {isAdmin ? (isOperatore ? "Centrale Operativa · Operatore" : "Centrale Operativa") : "Partner"}
    </div>
  );

  const homePath = isAdmin ? "/operativa" : "/partner";
  const isHome = location.pathname === homePath;
  const goBack = () => {
    if (window.history.state && window.history.state.idx > 0) navigate(-1);
    else navigate(homePath);
  };

  const crumbs = buildCrumbs({ pathname: location.pathname, navItems, homePath, isAdmin, crumbLabel });

  return (
    <div className="min-h-screen flex flex-col bg-[#F2F4F5]">
      {/* Barra superiore con menu a scomparsa (drawer) */}
      <header className="flex items-center justify-between h-16 px-4 md:px-6 border-b-2 border-nexus-gold bg-nexus-blue text-white sticky top-0 z-30">
        <div className="flex items-center gap-3 min-w-0">
          {!isHome && (
            <Button data-testid="global-back-button" size="sm" variant="ghost" className="gap-1 -ml-1 shrink-0 text-white hover:bg-white/10 hover:text-nexus-gold" onClick={goBack} aria-label="Torna alla pagina precedente">
              <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Indietro</span>
            </Button>
          )}
          {!isHome && crumbs.length > 1 && <Breadcrumbs crumbs={crumbs} onNavigate={navigate} />}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button data-testid="menu-button" size="icon" variant="ghost" className="text-white hover:bg-white/10 hover:text-nexus-gold" aria-label="Apri menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 flex flex-col">
              <SheetTitle className="sr-only">Menu di navigazione</SheetTitle>
              <SheetDescription className="sr-only">Sezioni della piattaforma Nexus Italia Group</SheetDescription>
              <div className="flex items-center gap-2 px-6 h-16 border-b-2 border-nexus-gold bg-nexus-blue">
                <div className="bg-white rounded-md px-2 py-1"><img src="/nexus_logo.png" alt="Nexus Italia Group" className="h-8 w-auto object-contain" /></div>
              </div>
              <div className="px-4 py-3 bg-white"><RoleBadge /></div>
              <div className="flex-1 overflow-y-auto">
                <NavList items={navItems} pathname={location.pathname}
                  onNavigate={(to) => { navigate(to); setMobileOpen(false); }} testPrefix="nav" />
              </div>
              <div className="p-4 border-t border-slate-200">
                <div className="mb-3 px-1">
                  <p className="text-sm font-semibold text-slate-800 truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                </div>
                <Button
                  data-testid="logout-button"
                  variant="outline"
                  className="w-full justify-start gap-2"
                  onClick={async () => { await logout(); navigate("/login"); }}
                >
                  <LogOut className="h-4 w-4" /> Esci
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          <div className="bg-white rounded-md px-2 py-1"><img src="/nexus_logo.png" alt="Nexus Italia Group" className="h-7 w-auto object-contain" /></div>
          <div className="hidden sm:block"><RoleBadge dark /></div>
        </div>
        <Button
          data-testid="logout-button-top"
          size="sm"
          variant="ghost"
          className="gap-2 text-white hover:bg-white/10 hover:text-nexus-gold"
          onClick={async () => { await logout(); navigate("/login"); }}
        >
          <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Esci</span>
        </Button>
      </header>

      {/* Main */}
      <main className="flex-1 overflow-auto p-4 md:p-8"><SectionErrorBoundary>{children}</SectionErrorBoundary></main>
    </div>
  );
};
